/**
 * ============================================================
 * Fur-Real — Embroidery Scroll Animation
 * FILE: assets/fur-real-embroidery.js
 * LOAD IN: layout/theme.liquid (before </body>, defer)
 *
 * ARCHITECTURE
 * ────────────
 * Consumer of window.__furRealFrameCache built by fur-real-splash.js.
 * No frames are loaded from network after the splash completes.
 * See fur-real-splash.js and ARCHITECTURE.md for full details.
 *
 * BUTTON ANIMATION
 * ────────────────
 * The Shop Now button (.fr-emb__shop-btn-wrap) starts invisible.
 * From frame BUTTON_START_FRAME it eases in over BUTTON_ANIM_FRAMES
 * frames, travelling upward by BUTTON_TRAVEL_VH viewport units.
 * After the animation completes it stays fully visible.
 * Logic mirrors the original initEmbroideryAnimation() exactly.
 *
 * PROGRESSIVE ACTIVATION (fix)
 * ─────────────────────────────
 * Previously, activate() awaited acquireFrames() finishing every
 * outstanding frame request before doing anything — so if the user
 * entered the site before all 244 frames finished downloading, the
 * section stayed inert and the canvas was left black until the very
 * last frame arrived.
 *
 * Frame loading is now fire-and-forget: acquireFrames() still kicks
 * off requests for every frame missing from the cache (unchanged),
 * but activate() no longer waits on them. The section activates and
 * starts drawing immediately using whatever is already cached.
 *
 * drawFrame() now falls back to the most recently available frame
 * when the exact requested frame isn't loaded yet, instead of
 * drawing nothing. tick() detects when the frame it's currently
 * sitting on finishes loading in the background and upgrades the
 * canvas from the fallback to the exact frame — no black flashes,
 * no waiting for 100%. Scroll-to-frame mapping, animation timing,
 * button behavior, and the cache format are all unchanged.
 * ============================================================
 */

(function FurRealEmbroidery() {
  'use strict';

  /* ── BUTTON CONFIG (mirrors original values exactly) ────────── */
  const BUTTON_START_FRAME  = 30;
  const BUTTON_ANIM_FRAMES  = 40;
  const BUTTON_TRAVEL_VH    = 12.5;

  /* ── FRAME URL BUILDER ──────────────────────────────────────── */
  function buildFrameUrls() {
    const isMobile = window.innerWidth <= 768;
    const urls     = [];
    if (isMobile) {
      for (let i = 1; i <= 244; i++) {
        urls.push(
          `https://cdn.shopify.com/s/files/1/0703/1318/1218/files/Mobile_frame_${String(i).padStart(3, '0')}.png`
        );
      }
    } else {
      for (let i = 1; i <= 244; i++) {
        urls.push(
          `https://cdn.shopify.com/s/files/1/0703/1318/1218/files/desktopFrame_${String(i).padStart(4, '0')}.png`
        );
      }
    }
    return urls;
  }

  /* ── DOM ────────────────────────────────────────────────────── */
  const wrapper   = document.querySelector('.fr-emb');
  const sticky    = document.querySelector('.fr-emb__sticky');
  const canvas    = document.querySelector('.fr-emb__canvas');
  const shopBtn   = document.getElementById('fr-emb-shop-btn');

  if (!wrapper || !canvas) return;

  const ctx = canvas.getContext('2d');

  /* ── STATE ──────────────────────────────────────────────────── */
  let frames       = [];
  let frameUrls    = [];
  let ready        = false;
  let currentFrame = -1;
  let rafId        = null;
  let resizeRafId  = null;

  /**
   * Fallback state for progressive rendering. lastGoodImg is the most
   * recently drawn real frame image — used to fill in for a requested
   * frame that hasn't finished loading yet, so the canvas never goes
   * black just because a frame is still in flight. exactFrameRendered
   * tracks whether the frame currently on screen is the actual
   * requested frame (true) or a stand-in fallback (false), so tick()
   * knows to upgrade the canvas once the real frame becomes available.
   */
  let lastGoodImg        = null;
  let exactFrameRendered = false;

  /* ── CANVAS SIZING ──────────────────────────────────────────── */
  function sizeCanvas() {
    if (!sticky) return;

    const rect = sticky.getBoundingClientRect();
    const dpr  = window.devicePixelRatio || 1;

    canvas.width  = Math.round(rect.width  * dpr);
    canvas.height = Math.round(rect.height * dpr);
    canvas.style.width  = rect.width  + 'px';
    canvas.style.height = rect.height + 'px';

    ctx.scale(dpr, dpr);

    if (ready && currentFrame >= 0) {
      drawFrame(currentFrame);
    }
  }

  function onResize() {
    cancelAnimationFrame(resizeRafId);
    resizeRafId = requestAnimationFrame(sizeCanvas);
  }

  /* ── FRAME REGISTRY CONSUMPTION ────────────────────────────── */
  /**
   * Kicks off requests for every frame not already in the splash's
   * cache. Fire-and-forget: each image updates frames[i] and its own
   * _ok flag independently via onload/onerror as it completes, so
   * callers do not need to wait on the returned promise to start
   * using whatever frames are already available (see activate()).
   */
  function acquireFrames() {
    frameUrls = window.__furRealFrameUrls || buildFrameUrls();

    const cache        = window.__furRealFrameCache || Object.create(null);
    const pendingLoads = [];

    frameUrls.forEach((src, i) => {
      const cached = cache[src];

      if (cached) {
        frames[i] = cached;
      } else {
        const img = new Image();
        frames[i] = img;

        pendingLoads.push(new Promise(resolve => {
          img.onload  = () => { img._ok = true;  resolve(); };
          img.onerror = () => { img._ok = false; resolve(); };
          img.src     = src;
        }));
      }
    });

    if (pendingLoads.length > 0) {
      console.warn(
        `[Fur-Real Embroidery] ${pendingLoads.length} frames not in cache — ` +
        'loading from network in the background. Was the splash screen skipped?'
      );
    }

    return Promise.all(pendingLoads);
  }

  /* ── FRAME RENDERING ────────────────────────────────────────── */
  /**
   * Draws the requested frame if it's ready. If it isn't (still
   * downloading), falls back to the most recently drawn real frame
   * instead of leaving the canvas blank/black. Sets exactFrameRendered
   * so tick() knows whether it needs to retry this index once the
   * real image finishes loading.
   */
  function drawFrame(index) {
    const requested = frames[index];
    let img = requested;

    if (img && img._ok) {
      lastGoodImg        = img;
      exactFrameRendered = true;
    } else if (lastGoodImg) {
      img                 = lastGoodImg;
      exactFrameRendered  = false;
    } else {
      // Nothing has loaded anywhere yet — nothing available to fall
      // back to. This only happens in the brief window before the
      // very first frame arrives.
      exactFrameRendered = false;
      return;
    }

    const cw = canvas.width  / (window.devicePixelRatio || 1);
    const ch = canvas.height / (window.devicePixelRatio || 1);

    const iw = img.naturalWidth  || img.width  || 1;
    const ih = img.naturalHeight || img.height || 1;

    const scale = Math.max(cw / iw, ch / ih);
    const sw    = iw * scale;
    const sh    = ih * scale;
    const sx    = (cw - sw) / 2;
    const sy    = (ch - sh) / 2;

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, sx, sy, sw, sh);
  }

  /* ── SCROLL → FRAME MAPPING ─────────────────────────────────── */
  function getScrollProgress() {
    const rect        = wrapper.getBoundingClientRect();
    const vh          = window.innerHeight;
    const scrollRange = rect.height - vh;

    if (scrollRange <= 0) return 0;

    const travelled = -rect.top;
    return Math.max(0, Math.min(1, travelled / scrollRange));
  }

  function scrollProgressToFrame(progress) {
    return Math.min(
      Math.round(progress * (frames.length - 1)),
      frames.length - 1
    );
  }

  /* ── BUTTON ANIMATION ───────────────────────────────────────── */
  /**
   * Mirrors the animateButton() logic from the original section exactly.
   *
   * CSS baseline for the button:
   *   Mobile:  left: 50%; transform: translateX(-50%) translateY(12.5vh)
   *   Desktop: right: 12%; transform: translateY(12.5vh)
   *
   * JS sets opacity and the translateY offset inline.
   * The translateX(-50%) for mobile must be preserved — we carry it
   * through on every inline assignment.
   */
  function animateButton(frame) {
    if (!shopBtn) return;

    const isDesktop    = window.innerWidth > 768;
    const baseTranslateX = isDesktop ? '' : 'translateX(-50%) ';

    if (frame < BUTTON_START_FRAME) {
      // Before animation starts: fully hidden, fully offset
      shopBtn.style.opacity   = '0';
      shopBtn.style.transform = `${baseTranslateX}translateY(${BUTTON_TRAVEL_VH}vh)`;
      shopBtn.classList.remove('fr-emb--btn-active');

    } else if (frame < BUTTON_START_FRAME + BUTTON_ANIM_FRAMES) {
      // Easing in: opacity 0→1, translateY TRAVEL→0
      const t       = (frame - BUTTON_START_FRAME) / BUTTON_ANIM_FRAMES;
      const eased   = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const offsetY = BUTTON_TRAVEL_VH * (1 - eased);

      shopBtn.style.opacity   = String(eased);
      shopBtn.style.transform = `${baseTranslateX}translateY(${offsetY}vh)`;
      if (eased > 0.5) shopBtn.classList.add('fr-emb--btn-active');

    } else {
      // Fully visible and settled
      shopBtn.style.opacity   = '1';
      shopBtn.style.transform = isDesktop ? '' : 'translateX(-50%)';
      shopBtn.classList.add('fr-emb--btn-active');
    }
  }

  /* ── RAF LOOP ───────────────────────────────────────────────── */
  function tick() {
    rafId = requestAnimationFrame(tick);

    const rect = wrapper.getBoundingClientRect();
    const vh   = window.innerHeight;

    // Section entirely off-screen — skip all work
    if (rect.bottom < 0 || rect.top > vh) return;

    const progress = getScrollProgress();
    const frame    = scrollProgressToFrame(progress);

    if (frame !== currentFrame) {
      currentFrame = frame;
      drawFrame(frame);
    } else if (!exactFrameRendered && frames[currentFrame] && frames[currentFrame]._ok) {
      // The frame the user is stopped on just finished loading in the
      // background (it was drawn as a fallback until now) — upgrade
      // the canvas to the real frame without waiting for a scroll event.
      drawFrame(currentFrame);
    }

    // Button runs every tick (progress can change without frame changing
    // at the ends of the range, and desktop/mobile check needs to stay live)
    animateButton(frame);
  }

  /* ── ACTIVATION ─────────────────────────────────────────────── */
  /**
   * Activates immediately using whatever frames are already cached,
   * rather than waiting for every frame to finish downloading.
   * acquireFrames() still requests all missing frames — it's just no
   * longer awaited here, so activation isn't gated on 100% completion.
   * Frames that arrive later are picked up by tick()'s fallback-upgrade
   * check above, and by drawFrame()'s own fallback when the user
   * scrolls to a not-yet-loaded frame.
   */
  function activate() {
    acquireFrames();

    sizeCanvas();
    window.addEventListener('resize', onResize, { passive: true });

    ready = true;

    currentFrame = 0;
    drawFrame(0);
    animateButton(0);

    if (sticky) sticky.classList.add('fr-emb--ready');

    tick();
  }

  /* ── BOOT ───────────────────────────────────────────────────── */
  if (window.__furRealSplashDone) {
    activate();
  } else {
    document.addEventListener('furreal:splashComplete', activate, { once: true });
  }

  /* ── CLEANUP ────────────────────────────────────────────────── */
  window.__furRealEmbroideryDestroy = function () {
    cancelAnimationFrame(rafId);
    cancelAnimationFrame(resizeRafId);
    window.removeEventListener('resize', onResize);
  };

}());